"use client";

import React, { useCallback, useEffect, useRef } from "react";
import {
  NotificationPayload,
  NotificationType,
} from "@/app/lib/models/NotificationModel";
import { Bounce, toast } from "@/app/lib/util/toast.client";
import { useNotifications } from "@/app/hooks/useNotifications";
import { isPersistedNotificationType } from "@/types/notification.types";
import useInitializeUserFromCookie from "@/app/components/hooks/useInitializeUserFromCookie";
import { usePathname, useRouter } from "next/navigation";
import { AppConstants } from "@/app/lib/util/AppConstants";
import logger from "@/lib/logger";
import { goToPage } from "@/app/lib/util/utilities.client";
import { shouldForceLogoutForNotification } from "@/app/lib/util/access-suspension.util";
import { confirmAndHandleAccessSuspended } from "@/app/services/access-suspension.client";

const TRAINEE_ALLOWED_NOTIFICATION_TYPES = new Set<string>([
  NotificationType.MEMBERS_ADDED,
  "members_added",
  NotificationType.ACCOUNT_UPGRADE,
  "account_upgrade",
  NotificationType.SUBSCRIPTION_UPDATE,
  "subscription_update",
  NotificationType.PAYMENT_SUCCESS,
  "payment_success",
]);

/** Types that should sync cookie / reload trainee access after payment. */
const SUBSCRIPTION_REFRESH_TYPES = new Set<string>([
  NotificationType.SUBSCRIPTION_UPDATE,
  "subscription_update",
  NotificationType.PAYMENT_SUCCESS,
  "payment_success",
  NotificationType.ACCOUNT_UPGRADE,
  "account_upgrade",
]);

export const NotificationHandler: React.FC = () => {
  const currentUrl = usePathname() as string;
  const router = useRouter();
  const userDetailsFromCookie = useInitializeUserFromCookie(currentUrl);
  const { subscribeToNotifications } = useNotifications();
  const recentNotificationKeysRef = useRef<Map<string, number>>(new Map());

  const handleNotification = useCallback(
    (notification: NotificationPayload) => {
      logger.info("Handling notification:", notification);

      const isTrainee =
        userDetailsFromCookie?.role === AppConstants.Roles.TRAINEE;
      const notificationType = String(notification.type);

      if (
        shouldForceLogoutForNotification(
          notificationType,
          userDetailsFromCookie?.role,
        )
      ) {
        if (userDetailsFromCookie?.email_id) {
          void confirmAndHandleAccessSuspended(
            userDetailsFromCookie.email_id,
            userDetailsFromCookie.login_session_id,
          );
        }
        return;
      }

      // Trainees only receive contact-added and subscription/access notifications.
      if (
        isTrainee &&
        !TRAINEE_ALLOWED_NOTIFICATION_TYPES.has(notificationType)
      ) {
        return;
      }

      const dispatchDataRefresh = () => {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("data-refresh-requested", {
              detail: {
                notification: {
                  type: notification.type,
                  title: notification.title,
                  message: notification.message,
                  timestamp: notification.timestamp,
                  data: (
                    notification as NotificationPayload & { data?: unknown }
                  ).data,
                },
              },
            }),
          );
        }
      };

      let icon = "🔔";
      let toastType: "info" | "success" | "warning" | "error" = "info";
      let shouldShowToast = true;

      if (isPersistedNotificationType(notificationType)) {
        icon =
          {
            analysis_ready: "📊",
            session_created: "💬",
            session_finished: "✅",
            account_upgrade: "🚀",
            generic: "🔔",
            kyc_pending: "📝",
            subscription_ending: "⏳",
            subscription_expired: "⚠️",
            payment_success: "✅",
            payment_failed: "❌",
            members_added: "👥",
            trainee_added: "👨‍🎓",
            subscription_update: "💳",
            user_update: "✨",
            red_flag: "🚨",
          }[notificationType] ?? "🔔";
        toastType =
          notificationType === "payment_success"
            ? "success"
            : notificationType === "red_flag"
              ? "error"
              : "info";
      } else {
        switch (notification.type as string) {
          case NotificationType.SYSTEM:
            icon = "📢";
            toastType = "info";
            break;
          case NotificationType.PROFILE_UPDATE:
            icon = "👤";
            toastType = "success";
            dispatchDataRefresh();
            shouldShowToast = false;
            break;
          case NotificationType.SUBSCRIPTION_UPDATE:
            icon = "💳";
            toastType = "success";
            break;
          case NotificationType.USER_UPDATE:
            icon = "✨";
            toastType = "info";
            dispatchDataRefresh();
            shouldShowToast = false;
            break;
          case NotificationType.MEMBERS_ADDED:
          case NotificationType.MEMBERS_UPDATED:
          case NotificationType.MEMBERS_DELETED:
            icon = "👥";
            toastType = "success";
            if (!isTrainee) {
              dispatchDataRefresh();
            }
            if (!isTrainee && typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("members-refresh-requested", {
                  detail: { notification },
                }),
              );
            }
            break;
          case NotificationType.CHATS_UPDATED:
          case NotificationType.SESSIONS_UPDATED:
            icon = "💬";
            toastType = "info";
            dispatchDataRefresh();
            shouldShowToast = false;
            break;
          case NotificationType.TRAINEE_ADDED:
          case "trainee_added":
            icon = "👨‍🎓";
            toastType = "success";
            dispatchDataRefresh();
            break;
          case NotificationType.TRAINEES_UPDATED:
            icon = "👨‍🎓";
            toastType = "info";
            dispatchDataRefresh();
            shouldShowToast = false;
            break;
          case NotificationType.GUARDIANS_UPDATED:
            icon = "🧑‍💼";
            toastType = "info";
            dispatchDataRefresh();
            shouldShowToast = false;
            break;
          case NotificationType.ANALYSIS_READY:
          case "analysis_ready":
            icon = "📊";
            toastType = "success";
            break;
          case NotificationType.SESSION_CREATED:
          case "session_created":
            icon = "💬";
            toastType = "info";
            break;
          case NotificationType.SESSION_FINISHED:
          case "session_finished":
            icon = "✅";
            toastType = "info";
            break;
          case NotificationType.ACCOUNT_UPGRADE:
          case "account_upgrade":
            icon = "🚀";
            toastType = "warning";
            break;
          case NotificationType.KYC_PENDING:
          case "kyc_pending":
            icon = "📝";
            toastType = "warning";
            break;
          case NotificationType.SUBSCRIPTION_ENDING:
          case "subscription_ending":
            icon = "⏳";
            toastType = "warning";
            break;
          case NotificationType.SUBSCRIPTION_EXPIRED:
          case "subscription_expired":
            icon = "⚠️";
            toastType = "error";
            break;
          case NotificationType.PAYMENT_SUCCESS:
          case "payment_success":
            icon = "✅";
            toastType = "success";
            break;
          case NotificationType.PAYMENT_FAILED:
          case "payment_failed":
            icon = "❌";
            toastType = "error";
            break;
          case NotificationType.RED_FLAG:
          case "red_flag":
            icon = "🚨";
            toastType = "error";
            break;
          default:
            icon = "🔔";
            toastType = "info";
        }
      }

      // Persisteds and live sockets both need this — otherwise Premium never
      // clears the trainee paywall until a manual refresh / re-login.
      if (SUBSCRIPTION_REFRESH_TYPES.has(notificationType)) {
        dispatchDataRefresh();
      }

      if (!document.hidden && shouldShowToast) {
        const message = `${icon} ${notification.title || "Notification"}`;
        const description = notification.message || "";
        const notificationKey = `${notification.type}|${notification.title || ""}|${notification.message || ""}`;
        const now = Date.now();
        const existingAt =
          recentNotificationKeysRef.current.get(notificationKey);
        if (existingAt && now - existingAt < 30000) {
          return;
        }
        recentNotificationKeysRef.current.set(notificationKey, now);

        const toastOptions = {
          toastId: notificationKey,
          position: "top-right" as const,
          autoClose:
            notification.type === NotificationType.SYSTEM ? 8000 : 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light" as const,
          transition: Bounce,
          pauseOnFocusLoss: false,
        } as Parameters<typeof toast.info>[1] & {
          render?: () => React.ReactNode;
          onClick?: () => void;
          style?: React.CSSProperties;
        };

        if (isTrainee) {
          // Toast is informational only — no click navigation for trainees.
          if (description) {
            toastOptions.render = () => (
              <div>
                <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                  {message}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {description}
                </div>
              </div>
            );
          }
        } else {
          const redirectUrl =
            typeof (
              notification as NotificationPayload & {
                data?: { redirect_url?: unknown };
              }
            ).data?.redirect_url === "string"
              ? (
                  notification as NotificationPayload & {
                    data: { redirect_url: string };
                  }
                ).data.redirect_url
              : null;

          if (description) {
            toastOptions.render = () => (
              <div>
                <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                  {message}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {description}
                </div>
              </div>
            );
          }

          if (redirectUrl?.startsWith("/dashboard/")) {
            toastOptions.style = { cursor: "pointer" };
            toastOptions.onClick = () => goToPage(router, redirectUrl);
          }
        }

        toast[toastType](message, toastOptions);
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("notification-received"));
      }

      if (
        typeof window !== "undefined" &&
        (
          window as Window & {
            KovaadAnalytics?: {
              logEvent: (name: string, data: unknown) => void;
            };
          }
        ).KovaadAnalytics
      ) {
        try {
          (
            window as Window & {
              KovaadAnalytics?: {
                logEvent: (name: string, data: unknown) => void;
              };
            }
          ).KovaadAnalytics?.logEvent("notification_received", {
            notificationType: notification.type,
            title: notification.title,
            timestamp: notification.timestamp,
          });
        } catch (error) {
          logger.warn("Failed to log notification event:", error);
        }
      }
    },
    [
      userDetailsFromCookie?.role,
      userDetailsFromCookie?.email_id,
      userDetailsFromCookie?.login_session_id,
      router,
    ],
  );

  useEffect(
    () => subscribeToNotifications(handleNotification),
    [subscribeToNotifications, handleNotification],
  );

  return null;
};

export default NotificationHandler;
